import { useState, useEffect } from 'react';
import {
  Box, Button, Divider, Slider, Typography,
} from '@mui/material';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import PropTypes from 'prop-types';
import i18n from '../../../common/i18n';
import { getCalculatorData } from '../../../../api/services';
import matrixResponse from '../../../calculator/matrixResponse.json';
import AmountSimulatorSkeleton from './AmountSimulatorSkeleton';
import { getLoanLink } from './homeData';
import {
  bodyFont,
  ctaSx,
  homeColors,
  homeEase,
  shadowSoft,
  smallTextSx,
  titleFont,
} from '../style/homeStyles';

const formatAmount = (value) => `$${value.toLocaleString('es-AR')}`;

// Misma regla que WtCalculator: la primera cuota vence el 6 del mes siguiente,
// y a partir del 21 se corre un mes más.
const getPaymentDate = () => {
  const dateNow = new Date();
  const day = dateNow.getDate();
  let plusMonth = 1;
  if (day >= 21) {
    plusMonth += 1;
  }
  dateNow.setDate(1);
  dateNow.setMonth(dateNow.getMonth() + plusMonth);
  const dueMonth = dateNow.toLocaleString('es-ES', { month: 'long' });
  return `6 de ${dueMonth} ${dateNow.getFullYear()}`;
};

const AmountSimulator = ({ isMobile }) => {
  const [amounts, setAmounts] = useState(undefined);
  const [amount, setAmount] = useState(undefined);
  const [dragging, setDragging] = useState(false);
  const loanLink = getLoanLink();

  // Arranca en el monto del medio de la matriz, igual que WtCalculator.
  const applyAmounts = (amountsAux) => {
    setAmounts(amountsAux);
    setAmount(amountsAux[Math.round((amountsAux.length - 1) / 2)]);
  };

  // Los montos disponibles salen de la matriz del simulador; si la API falla usamos la matriz local.
  const getCalculator = async () => {
    try {
      const { data } = await getCalculatorData();
      const hasAmounts = data && data.amounts && data.amounts.length > 0;
      applyAmounts(hasAmounts ? data.amounts : matrixResponse.amounts);
    } catch (error) {
      console.error('There was an error!', error);
      applyAmounts(matrixResponse.amounts);
    }
  };

  useEffect(() => {
    getCalculator();
  }, []);

  const pushEvent = () => {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      event: 'select_item',
      ecommerce: {
        items: [{
          item_name: 'Tradicional Web',
        }],
      },
    });
  };

  return (
    <Box sx={{
      // En desktop la tarjeta ocupa su propia columna y se alinea a la derecha de la grilla.
      marginTop: isMobile ? '26px' : 0,
      marginLeft: isMobile ? 0 : 'auto',
      padding: isMobile ? '20px' : '26px',
      maxWidth: isMobile ? 'none' : 440,
      borderRadius: '20px',
      background: 'linear-gradient(180deg, #FFFFFF 0%, #FFFBFD 100%)',
      boxShadow: shadowSoft,
    }}
    >
      {amount === undefined ? <AmountSimulatorSkeleton isMobile={isMobile} /> : (
        <>
          <Typography
            component="p"
            sx={{
              fontFamily: titleFont,
              fontWeight: 700,
              fontSize: isMobile ? 18 : 20,
              lineHeight: 1.2,
              letterSpacing: '-0.02em',
              color: homeColors.ink,
            }}
          >
            {i18n.homeHeroSimulatorTitle}
          </Typography>
          <Typography
            component="p"
            sx={{
              ...smallTextSx, fontSize: isMobile ? 12 : 13, marginTop: '4px',
            }}
          >
            {i18n.homeHeroSimulatorSubtitle}
          </Typography>

          <Typography
            component="p"
            sx={{
              fontFamily: titleFont,
              fontWeight: 700,
              fontSize: isMobile ? 44 : 56,
              lineHeight: 1,
              letterSpacing: '-0.04em',
              textAlign: 'center',
              marginTop: isMobile ? '18px' : '24px',
              color: dragging ? homeColors.pink : homeColors.ink,
              transition: `color .2s ${homeEase}`,
            }}
          >
            {formatAmount(amount)}
          </Typography>

          <Slider
            value={amount}
            min={Math.min(...amounts)}
            max={Math.max(...amounts)}
            step={null}
            marks={amounts.map((val) => ({ value: val }))}
            onChange={(event, value) => setAmount(value)}
            onMouseDown={() => setDragging(true)}
            onTouchStart={() => setDragging(true)}
            onChangeCommitted={() => setDragging(false)}
            aria-label={i18n.homeHeroAmountLabel}
            sx={{
              marginTop: isMobile ? '16px' : '20px',
              padding: '10px 0',
              color: homeColors.pink,
              // El thumb sobresale del carril: se descuenta su ancho para alinear los extremos
              // con los montos mínimo y máximo.
              width: `calc(100% - ${isMobile ? 26 : 30}px)`,
              marginX: 'auto',
              display: 'block',
              '& .MuiSlider-rail': {
                height: 12,
                opacity: 1,
                backgroundColor: homeColors.line,
                boxShadow: 'inset 0 1px 2px rgba(31,27,29,0.06)',
              },
              '& .MuiSlider-track': {
                height: 12,
                border: 'none',
                background: `linear-gradient(90deg, ${homeColors.pink} 0%, #FF4B95 100%)`,
              },
              // Los marks solo hacen que el slider salte entre montos: no se dibujan.
              '& .MuiSlider-mark': { display: 'none' },
              '& .MuiSlider-thumb': {
                width: isMobile ? 26 : 30,
                height: isMobile ? 26 : 30,
                backgroundColor: homeColors.pinkSoft,
                border: `3px solid ${homeColors.pink}`,
                boxShadow: '0 4px 10px rgba(218,0,103,0.25)',
                '&:hover, &.Mui-focusVisible': { boxShadow: '0 4px 14px rgba(218,0,103,0.35)' },
                '&.Mui-active': { transform: 'translate(-50%, -50%) scale(1.08)' },
              },
            }}
          />

          <Box sx={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px',
          }}
          >
            <Typography component="span" sx={{ ...smallTextSx, fontSize: isMobile ? 12 : 13, color: homeColors.ink2 }}>
              {formatAmount(Math.min(...amounts))}
            </Typography>
            <Typography component="span" sx={{ ...smallTextSx, fontSize: isMobile ? 12 : 13, color: homeColors.ink2 }}>
              {formatAmount(Math.max(...amounts))}
            </Typography>
          </Box>

          <Divider sx={{ borderColor: homeColors.line, marginTop: isMobile ? '16px' : '20px' }} />

          <Box sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: '12px',
            marginTop: isMobile ? '14px' : '16px',
          }}
          >
            <Typography component="span" sx={{ ...smallTextSx, fontSize: isMobile ? 12 : 13 }}>
              {i18n.homeHeroFirstDueDate}
            </Typography>
            <Typography
              component="span"
              sx={{
                fontFamily: bodyFont,
                fontSize: isMobile ? 12 : 13,
                fontWeight: 700,
                lineHeight: 1.5,
                color: homeColors.ink,
              }}
            >
              {getPaymentDate()}
            </Typography>
          </Box>

          <Button
            component="a"
            href={loanLink}
            target="_blank"
            rel="noopener noreferrer"
            onClick={pushEvent}
            sx={{
              ...ctaSx, marginTop: isMobile ? '16px' : '20px', width: '100%', fontFamily: bodyFont,
            }}
          >
            {i18n.homeHeroCta}
          </Button>

          <Box sx={{
            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', marginTop: '14px',
          }}
          >
            <LockOutlinedIcon sx={{ fontSize: 16, color: homeColors.ink3 }} />
            <Typography component="span" sx={{ ...smallTextSx, fontSize: 12 }}>
              {i18n.homeHeroSecure}
            </Typography>
          </Box>
        </>
      )}
    </Box>
  );
};

AmountSimulator.propTypes = {
  isMobile: PropTypes.bool,
};

AmountSimulator.defaultProps = {
  isMobile: false,
};

export default AmountSimulator;
